import { 
  Component, 
  EventEmitter, 
  Input, 
  Output, 
  ViewChild, 
  ElementRef, 
  HostListener, 
  ChangeDetectionStrategy,
  TemplateRef,
  ContentChild,
  OnChanges,
  AfterViewInit,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { animate, state, style, transition, trigger } from '@angular/animations';

// Types
export interface FontSizeType {
  [key: number]: string;
}

export interface FontWeightType {
  [key: number]: string;
}

export interface SizeType {
  [key: string]: string;
}

export interface PaddingType {
  [key: string]: string;
}

export interface DropdownOption {
  label: string;
  value: any;
}

export interface ButtonVariant {
  button: string;
  hover?: string;
}

// Style constants
export class ButtonStyleConstants {
  static readonly SIZES: SizeType = {
    xs: 'h-[15px] text-xs',
    sm: 'h-[20px] text-sm',
    xmd: 'h-[23px] text-sm',
    xmd2: 'h-[24px] text-sm',
    md: 'h-[25px] text-base',
    lg: 'h-[26px] text-base',
    xl: 'h-[27px] text-base',
    '2xl': 'h-[31px] text-lg',
    '3xl': 'h-[32px] text-lg',
    '4xl': 'h-[33px] text-lg',
    '5xl': 'h-[34px] text-xl',
    '6xl': 'h-[36px] text-xl',
    '7xl': 'h-[38px] text-xl',
    '8xl': 'h-[40px] text-2xl',
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-4 text-base',
    large: 'py-2.5 px-5 text-lg'
  };

  static readonly FONT_WEIGHTS: FontWeightType = {
    100: 'font-thin',
    200: 'font-extralight',
    300: 'font-light',
    400: 'font-normal',
    500: 'lg:font-medium md:font-medium sm:font-medium xs:font-medium font-normal ',
    600: 'lg:font-semibold md:font-semibold sm:font-semibold xs:font-semibold font-medium',
    700: 'lg:font-bold md:font-bold sm:font-bold xs:font-bold font-semibold',
    800: 'font-extrabold',
    900: 'font-black'
  };

  static readonly FONT_SIZES: FontSizeType = {
    10: 'text-[10px]',
    12: 'text-[12px]',
    14: 'text-[14px]',
    16: 'text-[16px]',
    17: 'lg:text-[17px] md:text-[17px] sm:text-[17px] xs:text-[17px] text-[16px]',
    18: 'lg:text-[18px] md:text-[18px] sm:text-[18px] xs:text-[18px] text-[16px]',
    19: 'lg:text-[19px] md:text-[19px] sm:text-[19px] xs:text-[19px] text-[16px]',
    20: 'lg:text-[20px] md:text-[20px] sm:text-[20px] xs:text-[20px] text-[16px]',
    21: 'lg:text-[21px] md:text-[21px] sm:text-[21px] xs:text-[21px] text-[16px]',
    22: 'lg:text-[22px] md:text-[22px] sm:text-[22px] xs:text-[22px] text-[16px]',
    23: 'lg:text-[23px] md:text-[23px] sm:text-[23px] xs:text-[23px] text-[16px]',
    24: 'lg:text-[24px] md:text-[24px] sm:text-[24px] xs:text-[24px] text-[16px]',
    28: 'lg:text-[28px] md:text-[28px] sm:text-[28px] xs:text-[28px] text-[16px]',
    32: 'lg:text-[32px] md:text-[32px] sm:text-[32px] xs:text-[32px] text-[16px]',
    36: 'lg:text-[36px] md:text-[36px] sm:text-[36px] xs:text-[36px] text-[16px]',
    40: 'lg:text-[40px] md:text-[40px] sm:text-[40px] xs:text-[40px] text-[16px]',
    48: 'lg:text-[48px] md:text-[48px] sm:text-[48px] xs:text-[48px] text-[16px]',
    56: 'lg:text-[56px] md:text-[56px] sm:text-[56px] xs:text-[56px] text-[16px]',
    64: 'lg:text-[64px] md:text-[64px] sm:text-[64px] xs:text-[64px] text-[16px]'
  };

  static readonly PADDING: PaddingType = {
    px16_py11: "px-[12px] lg:py-[0.719rem] md:py-[0.719rem] sm:py-[0.719rem] xs:py-[0.719rem] py-[11.5px] lg:px-[1rem] md:px-[1rem] sm:px-[1rem] xs:px-[1rem]",
    px16_py11b: "px-[12px] lg:px-[14.61px] md:px-[14.61px] sm:px-[14.61px] xs:px-[14.61px] lg:py-[10.5px] md:py-[10.5px] sm:py-[10.5px] xs:py-[10.5px] py-[10.5px]",
    px24_py18: "px-[1.5rem] lg:py-[1.125rem] md:py-[1.125rem] sm:py-[1.125rem] xs:py-[1.125rem] py-[11.5px]",
    px24_py16: "lg:px-[1.506rem] px-[1.3rem] lg:py-[0.9375rem] md:py-[0.9375rem] sm:py-[0.9375rem] xs:py-[0.9375rem] py-[11.5px]",
    px24_py14 : "px-[1.5rem] lg:py-[0.875rem] md:py-[0.875rem] sm:py-[0.875rem] xs:py-[0.875rem] py-[11.5px]",
    px26_py16: "px-[1.657rem] lg:py-[0.625rem] md:py-[0.625rem] sm:py-[0.625rem] xs:py-[0.625rem] py-[11.5px]",
    px48_py22: "px-[3rem] lg:py-[1.088rem] md:py-[1.088rem] sm:py-[1.088rem] xs:py-[1.088rem] py-[11.5px]",
    py_13: "lg:py-[0.84375rem] md:py-[0.84375rem] sm:py-[0.84375rem] xs:py-[0.84375rem] py-[11.5px]",
    py14: "lg:py-[0.875rem] md:py-[0.875rem] sm:py-[0.875rem] xs:py-[0.875rem] py-[11.5px]",
    px24_py_14: "px-[1.5rem] lg:py-[0.90625rem] md:py-[0.90625rem] sm:py-[0.90625rem] xs:py-[0.90625rem] py-[11.5px]",
    px24_py_14sm: "md:px-[1.5rem] md:py-[0.90625rem] lg:px-[1.5rem] lg:py-[0.90625rem] sm:px-[1.5rem] sm:py-[0.90625rem] px-[0.5rem] py-[11.5px]"
  };

  static readonly RADII = {
    small: 'rounded-sm',
    medium: 'rounded-md',
    large: 'rounded-[12px]',
    full: 'rounded-full'
  };

  static readonly COLORS = {
    primary: {
      base: 'bg-primary-500 text-white',
      hover: 'hover:bg-primary-600',
      active: 'active:bg-primary-700',
      border: 'border-primary-500'
    },
    secondary: {
      base: 'bg-secondary-500 text-white',
      hover: 'hover:bg-secondary-600',
      active: 'active:bg-secondary-700',
      border: 'border-secondary-500 border-2'
    },
    error: {
      base: 'bg-danger-500 text-white',
      hover: 'hover:bg-danger-600',
      active: 'active:bg-danger-700',
      border: 'border-danger-500'
    },
    success: {
      base: 'bg-success-500 text-white',
      hover: 'hover:bg-success-600',
      active: 'active:bg-success-700',
      border: 'border-success-500'
    },
    warning: {
      base: 'bg-warning-500 text-white',
      hover: 'hover:bg-warning-600',
      active: 'active:bg-warning-700',
      border: 'border-warning-500'
    },
    basic: {
      base: 'bg-gray-100 text-gray-800',
      hover: 'hover:bg-gray-200',
      active: 'active:bg-gray-300',
      border: 'border-gray-300'
    },
    link: {
      base: 'text-primary-500 bg-transparent',
      hover: 'hover:text-primary-600 hover:underline',
      active: 'active:text-primary-700',
      border: 'border-transparent'
    },
    text: {
      base: 'text-gray-800 bg-transparent',
      hover: 'hover:bg-gray-100',
      active: 'active:bg-gray-200',
      border: 'border-transparent'
    },
    blue : {
      base: 'bg-blue-100 text-white',
      hover: 'hover:bg-blue-200',
      active: 'active:bg-blue-300',
      border: 'border-blue-300'
    },
    blue2 : {
      base : 'bg-blue-300 text-white  border-[1px]',
      hover: 'hover:bg-blue-50  border-[1px]',
      active: 'active:bg-blue-50  border-[1px]',
      border: 'border-blue-200 border-[1px]'  
    },
    plain : {
      base : 'bg-white text-primary-500',
      hover: 'hover:bg-gray-100',
      active: 'active:bg-primary-700',
      border: ''
    },
    gray : {
      base : 'bg-gray-1000 text-gray-1100',
      hover: 'hover:bg-gray-200',
      active: 'active:bg-gray-300',
      border: ''
    }
  };

  static readonly VARIANTS = {
    contained: {
      base: 'shadow-sm  bg-primary-500 text-white',
      hover: 'hover:shadow-md',
      active: 'active:shadow-none'
    },
    outlined: {
      base: 'border bg-transparent text-primary-500',
      hover: 'hover:bg-opacity-10',
      active: 'active:bg-opacity-20'
    },
    text: {
      base: 'text-primary-500 bg-transparent',
      hover: '',
      active: ''
    },
    raised: {
      base: 'shadow-md bg-primary-500 text-white',
      hover: 'hover:shadow-lg',
      active: 'active:shadow-sm'
    },
    plain: {
      base: 'bg-transparent',
      hover: '',
      active: ''
    },
    link: {
      base: 'underline text-primary-500 bg-transparent',
      hover: '',
      active: ''
    }
  };
}

@Component({
  selector: 'NiFormButton',
  templateUrl: './ni-button.component.html',
  styleUrls: ['./ni-button.component.css'],
  standalone: true,
  imports: [CommonModule, NiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('buttonPress', [
      state('pressed', style({
        transform: 'scale(0.95)',
        transition: 'transform 0.1s ease-out'
      })),
      state('released', style({
        transform: 'scale(1)',
        transition: 'transform 0.15s ease-in'
      })),
      transition('released => pressed', [
        animate('100ms ease-out')
      ]),
      transition('pressed => released', [
        animate('150ms ease-in')
      ])
    ]),
    trigger('contentFade', [
      state('in', style({
        opacity: 1,
        transform: 'translateY(0)'
      })),
      state('out', style({
        opacity: 0,
        transform: 'translateY(-2px)'
      })),
      transition('out => in', [
        animate('200ms ease-out')
      ]),
      transition('in => out', [
        animate('150ms ease-in')
      ])
    ])
  ]
})
export class NiButtonComponent implements  OnChanges, AfterViewInit {
  // Constants
  sizes = ButtonStyleConstants.SIZES;
  fontWeights = ButtonStyleConstants.FONT_WEIGHTS;
  fontSizes = ButtonStyleConstants.FONT_SIZES;
  paddingSizes = ButtonStyleConstants.PADDING;
  radii = ButtonStyleConstants.RADII;
  variants = ButtonStyleConstants.VARIANTS;
  colors = ButtonStyleConstants.COLORS;

  // Button Configuration Inputs
  @Input() color: keyof typeof ButtonStyleConstants.COLORS = 'primary';
  @Input() variant: keyof typeof ButtonStyleConstants.VARIANTS = 'contained';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() paddingSize: keyof PaddingType = 'p9';
  @Input() size: keyof SizeType = 'medium';
  @Input() radius: keyof typeof ButtonStyleConstants.RADII = 'large';
  @Input() fullWidth: boolean = false;
  
  // Content Inputs
  @Input() label: string = '';
  @Input() text: string = '';
  @Input() leadingIcon: string = '';
  @Input() trailingIcon: string = '';
  @Input() leadingIconClass: string = 'w-4 h-4';
  @Input() trailingIconClass: string = 'w-4 h-4';

  // Style Inputs
  @Input() fontSize: keyof FontSizeType = 16;
  @Input() fontWeight: keyof FontWeightType = 400;
  @Input() isRounded: boolean = false;
  @Input() lineHeight: string = '';

  // Behavior Inputs
  @Input() disabled: boolean| null = false;
  @Input() isDisabled: boolean = false;
  @Input() isFocused: boolean = false;
  @Input() loading: boolean = false;
  @Input() loaderSize: string = 'w-4 h-4';
  @Input() loaderColor: string = 'text-white';
  @Input() preserveWidthDuringLoading: boolean = true;


  // Accessibility
  @Input() ariaLabel?: string;

  // New Inputs
  @Input() icon: string = '';
  @Input() iconPos: 'left' | 'right' = 'left';
  @Input() badge: string = '';
  @Input() badgeClass: string = '';
  @Input() batchColur: 'primary' | 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'blue' | 'plain'  | 'blue2' | 'gray' = 'primary';
  @Input() loadingIcon: string = '';
  @Input() autofocus: boolean = false;
  @Input() tabindex: number = 0;
  @Input() buttonProps: any = {};
  @Input() styleClass: string = '';
  @Input() style: { [klass: string]: any } = {};

  // Outputs
  @Output() onClick = new EventEmitter<Event>();
  @Output() onLeadingIconClick = new EventEmitter<MouseEvent>();
  @Output() onTrailingIconClick = new EventEmitter<MouseEvent>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();

  @ViewChild('buttonElement') buttonElement!: ElementRef<HTMLButtonElement>;

  // animation state
  buttonState: 'pressed' | 'released' = 'released';
  hoverState: 'hover' | 'noHover' = 'noHover';

  // ContentChild for custom icon and loadingIcon
  @ContentChild('icon', { read: TemplateRef }) iconTemplate?: TemplateRef<any>;
  @ContentChild('loadingIcon', { read: TemplateRef }) loadingIconTemplate?: TemplateRef<any>;

  constructor(private readonly elementRef: ElementRef) {}

  get effectiveLabel(): string {
    return this.label || this.text;
  }

  get isButtonDisabled(): boolean {
    return this.disabled || this.isDisabled;
  }

  get showLoader(): boolean {
    return this.loading && !this.isButtonDisabled;
  }

  get showContent(): boolean {
    return !this.loading || this.isButtonDisabled;
  }

  get buttonClasses(): string {
    // Static Tailwind class mapping for variants and colors
    const color = this.color || 'primary';
    const variant = this.variant || 'contained';
    const staticMap: any = {
      contained: {
        primary: 'bg-primary-500  text-[#FAFAFA] hover:bg-primary-600 active:bg-primary-700',
        secondary: 'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700',
        success: 'bg-success-500 text-white hover:bg-success-600 active:bg-success-700',
        error: 'bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700',
        warning: 'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700',
        text: 'bg-transparent text-primary-500 hover:bg-primary-50',
        plain : 'bg-white text-primary-500 hover:bg-gray-100',
        gray : 'bg-gray-1000 text-gray-1100 hover:bg-gray-200 active:bg-gray-300',
        blue2 : 'bg-blue-300 border border-blue-200 text-blue-200 hover:bg-blue-50',
      },
      outlined: {
        primary: 'bg-transparent border border-primary-500 text-primary-500 hover:bg-primary-50',
        secondary: 'bg-transparent border border-secondary-500 border-2 text-secondary-500 hover:bg-secondary-50',
        success: 'bg-transparent border border-success-500 text-success-500 hover:bg-success-50',
        error: 'bg-transparent border border-danger-500 text-danger-500 hover:bg-danger-50',
        warning: 'bg-transparent border border-warning-500 text-warning-500 hover:bg-warning-50',
        text: 'bg-transparent border border-primary-500 text-primary-500 hover:bg-primary-50',
        blue : 'bg-transparent border border-blue-100 text-blue-100 hover:bg-blue-50 box-border',
        blue2 : 'bg-transparent border border-blue-200 text-blue-200 hover:bg-blue-50',
      },
      text: {
        primary: 'bg-transparent text-primary-500 hover:bg-primary-50',
        secondary: 'bg-transparent text-secondary-500 hover:bg-secondary-50',
        success: 'bg-transparent text-success-500 hover:bg-success-50',
        error: 'bg-transparent text-danger-500 hover:bg-danger-50',
        warning: 'bg-transparent text-warning-500 hover:bg-warning-50',
        text: 'bg-transparent text-primary-500 hover:bg-primary-50',
      },
      plain: {
        primary: 'bg-transparent',
        secondary: 'bg-transparent',
        success: 'bg-transparent',
        error: 'bg-transparent',
        warning: 'bg-transparent',
        text: 'bg-transparent',
      },
      link: {
        primary: 'bg-transparent underline text-primary-500 hover:text-primary-600',
        secondary: 'bg-transparent underline text-secondary-500 hover:text-secondary-600',
        success: 'bg-transparent underline text-success-500 hover:text-success-600',
        error: 'bg-transparent underline text-danger-500 hover:text-danger-600',
        warning: 'bg-transparent underline text-warning-500 hover:text-warning-600',
        text: 'bg-transparent underline text-primary-500 hover:text-primary-600',
      },
      raised: {
        primary: 'bg-primary-500 text-white shadow-lg hover:bg-primary-600 active:bg-primary-700',
        secondary: 'bg-secondary-500 text-white shadow-lg hover:bg-secondary-600 active:bg-secondary-700',
        success: 'bg-success-500 text-white shadow-lg hover:bg-success-600 active:bg-success-700',
        error: 'bg-danger-500 text-white shadow-lg hover:bg-danger-600 active:bg-danger-700',
        warning: 'bg-warning-500 text-white shadow-lg hover:bg-warning-600 active:bg-warning-700',
          text: 'bg-primary-500 text-white shadow-lg hover:bg-primary-600 active:bg-primary-700',
      },
  
    };
    const base = [
      'flex items-center justify-center transition-all duration-200',
      this.fullWidth ? 'w-full' : '',
      this.sizes[this.size],
      this.fontWeights[this.fontWeight],
      this.fontSizes[this.fontSize],
      this.paddingSizes[this.paddingSize] ,
      this.radii[this.radius],
      this.isRounded ? 'rounded-full' : '',
      this.isButtonDisabled ? 'opacity-50 cursor-not-allowed' : '',
      this.styleClass,
      staticMap[variant]?.[color] || staticMap['contained']['primary']
    ];
    return base.filter(Boolean).join(' ');
  }

  handleClick(event: Event) {
    if (!this.isButtonDisabled) {
      this.buttonState = 'pressed';
      setTimeout(() => {
        this.buttonState = 'released';
        this.onClick.emit(event);
      }, 150);
    }
  }

  @HostListener('mouseenter') 
  onMouseEnter() {
    this.hoverState = 'hover';
  }

  @HostListener('mouseleave') 
  onMouseLeave() {
    this.hoverState = 'noHover';
  }

  handleLeadingIconClick(event: MouseEvent) {
    event.stopPropagation();
    this.onLeadingIconClick.emit(event);
  }

  handleTrailingIconClick(event: MouseEvent) {
    event.stopPropagation();
    this.onTrailingIconClick.emit(event);
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
  }

  focus() {
    if (this.buttonElement) {
      this.buttonElement.nativeElement.focus();
    }
  }



  ngAfterViewInit(): void {
    if (this.autofocus && this.buttonElement) {
      setTimeout(() => this.buttonElement.nativeElement.focus(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Forwards changes to native button if needed
  }

  handleFocus(event: FocusEvent) {
    this.onFocus.emit(event);
  }

  handleBlur(event: FocusEvent) {
    this.onBlur.emit(event);
  }

  get badgeColorClass(): string {
    switch (this.batchColur) {
      case 'primary': return 'bg-primary-500 text-white';
      case 'success': return 'bg-green-500 text-white';
      case 'info': return 'bg-blue-500 text-white';
      case 'warn': return 'bg-yellow-500 text-white';
      case 'danger': return 'bg-red-500 text-white';
      case 'secondary': return 'bg-gray-500 text-white';
      default: return 'bg-primary-500 text-white';
    }
  }

  get computedLoaderColor(): string {
    switch (this.variant) {
      case 'outlined':
      case 'text':
      case 'link':
      case 'plain':
        return /text-[^ ]+/.exec(this.colors[this.color]?.base ?? '')?.[0] ?? 'text-primary-500';
      case 'raised':
      case 'contained':
      default:
        return 'text-white';
    }
  }
}

